import React from 'react';
import { withApollo } from '@apollo/react-hoc';
import PropTypes from 'prop-types';
import FocusLock from 'react-focus-lock';
import { FormattedMessage, injectIntl } from 'react-intl';
import keyboardKey from 'keyboard-key';

import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Dimmer from '../../../components/Dimmer';
import Loader from '../../../components/Loader';
import Checkbox from '../../../components/Checkbox';
import Icon from '../../../components/Icon';
import Message from '../../../components/Message';
import Label from '../../../components/Label';
import SuggestionList from './suggestion-list';
import messages from '../messages';
import { enforceCollegeLocationFormat } from '../../../utils/location-utils';
import fetchLocationSuggestionQuery from '../graphql/locationSuggestionQuery';
import fetchLocationSuggestionQueryWithPlaceId from '../graphql/locationSuggestionQueryWithPlaceId';
import fetchGeocodeLocationQuery from '../graphql/geocodeLocationQuery';
import fetchGeocodeLocationQueryWithPlaceId from '../graphql/geocodeLocationQueryWithPlaceId';
import { USE_PLACEID_FOR_GEOCODE_API } from '../../Authorization/constants';

import '../styles.css';

export class EditLocationComponent extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    client: PropTypes.object,
    location: PropTypes.object,
    isLoading: PropTypes.bool,
    isUpdateButtonDisabled: PropTypes.bool,
    requestError: PropTypes.object,
    clearRequestError: PropTypes.func.isRequired,
    showLocations: PropTypes.func.isRequired,
    saveLocation: PropTypes.func.isRequired,
    deleteLocation: PropTypes.func.isRequired,
    onClosePopup: PropTypes.func.isRequired,
    enabledFeatures: PropTypes.array.isRequired,
  };

  constructor(props) {
    super(props);

    let address = null;
    if (this.props.location) {
      const { city, postalCode, state, street } = this.props.location;
      address = `${street}, ${city}, ${state}, ${postalCode}`;
    }

    this.state = {
      name: (props.location || {}).name || '',
      address,
      invalidLocationName: false,
      invalidLocationAddress: false,
      shouldSaveLocation: false,
      isDeleting: false,
      isUpdateButtonDisabled: true,

      showingSuggestions: false,
      fetchingSuggestions: false,
      suggestionsFocusLock: true,
      suggestions: [],
      selectedSuggestion: null,

      fetchingGeocodeLocation: false,
      geocodeLocation: null,
      formattedGeocodeLocation: null,

    };
  }

  onNameChange = (name) => {
    this.setState({ name: name.replace(/\s+/g, ' '), invalidLocationName: false, isUpdateButtonDisabled: false });
    this.props.clearRequestError();
  }

  onAddressChange = (address) => {
    this.setState({ address, geocodeLocation: null, formattedGeocodeLocation: null, isUpdateButtonDisabled: false });
    this.props.clearRequestError();
    if (address.length >= 3 && address !== this.state.selectedSuggestion) {
      this.setState({ selectedSuggestion: null });
      this.fetchLocationSuggestion(address);
    }
  }

  fetchLocationSuggestion = (address) => {
    this.setState({ fetchingSuggestions: true });
    this.props.client.query({
      query: (
        this.props.enabledFeatures.includes(USE_PLACEID_FOR_GEOCODE_API) ?
          fetchLocationSuggestionQueryWithPlaceId :
          fetchLocationSuggestionQuery
      ),
      variables: {
        input: address,
      },
    }).then((response) => this.onLocationSuggestionFetched(response));
  }

  onLocationSuggestionFetched = (response) => {
    const rawSuggestions = response.data.viewer.locationSuggestion;
    const suggestions = this.props.enabledFeatures.includes(USE_PLACEID_FOR_GEOCODE_API) ?
      rawSuggestions :
      rawSuggestions.map((suggestion) => suggestion.street);
    this.setState({ fetchingSuggestions: false, suggestions, showingSuggestions: !!suggestions.length });
  }

  onSuggestionSelected = (suggestion) => {
    this.setState({
      address: this.props.enabledFeatures.includes(USE_PLACEID_FOR_GEOCODE_API) ? suggestion.street : suggestion,
      selectedSuggestion: this.props.enabledFeatures.includes(USE_PLACEID_FOR_GEOCODE_API) ? suggestion.street : suggestion,
      suggestions: [],
      showingSuggestions: false,
      suggestionsFocusLock: true,
    });
    this.fetchGeocodeLocation(suggestion);
  }

  fetchGeocodeLocation = (address) => {
    this.setState({ geocodeLocation: null, fetchingGeocodeLocation: true });
    this.props.client.query({
      query: this.props.enabledFeatures.includes(USE_PLACEID_FOR_GEOCODE_API) ?
        fetchGeocodeLocationQueryWithPlaceId :
        fetchGeocodeLocationQuery,
      variables: this.props.enabledFeatures.includes(USE_PLACEID_FOR_GEOCODE_API) ?
        { placeId: address.placeId } :
        { input: address },
    }).then((response) => this.onGeocodeLocationFetched(response));
  }

  onGeocodeLocationFetched = (response) => {
    const { geocodeLocation } = response.data.viewer;
    const { street, city, state, postalCode, country } = geocodeLocation;
    const isGeocodeInvalid = !!(!street || !city || !state || !postalCode || !country);
    this.setState({
      fetchingGeocodeLocation: false,
      geocodeLocation,
      formattedGeocodeLocation: enforceCollegeLocationFormat(geocodeLocation),
      invalidLocationAddress: isGeocodeInvalid || (country !== 'US' && country !== 'CA'),
    });
  };

  validateLocation = () => {
    const { shouldSaveLocation } = this.state;

    const name = this.state.name.trim();
    const invalidLocationName = name.length === 0;
    if (invalidLocationName) {
      this.setState({ invalidLocationName });
      return;
    }

    const address = this.state.address.trim();
    const invalidLocationAddress = address.length === 0;
    if (invalidLocationAddress) {
      this.setState({ invalidLocationAddress });
      return;
    }

    if (!this.state.geocodeLocation) {
      const { city, postalCode, state, street } = (this.props.location || {});
      const addressFromLocation = this.props.location && `${street}, ${city}, ${state}, ${postalCode}`.trim();

      if (addressFromLocation !== address) {
        this.fetchGeocodeLocation(address);
        return;
      }
    }

    if (this.state.invalidLocationAddress) return;
    this.props.saveLocation({
      ...this.props.location,
      ...this.state.geocodeLocation,
      name,
      shouldSaveLocation,
    });
  }

  hideSuggestions = () => {
    this.setState({
      showingSuggestions: false,
      fetchingSuggestions: false,
      suggestionsFocusLock: true,
    });
  }

  onKeyDownInput = (event) => {
    const keyCode = keyboardKey.getCode(event);

    if (keyCode === keyboardKey.Tab) {
      this.hideSuggestions();
      return;
    }

    if (keyCode === keyboardKey.ArrowDown) {
      if (this.state.showingSuggestions) {
        document.querySelector('ul#suggestion-list li:nth-child(1) > button').focus();
        this.setState({ suggestionsFocusLock: false });
      }
    }
  }

  renderNameWarning = () => {
    if (!this.state.invalidLocationName) return null;
    return (
      <div data-cy="invalid-name-warning">
        <Label role="tooltip" basic color="red" pointing>
          <FormattedMessage {...messages.missingName} />
        </Label>
      </div>
    );
  }

  renderAddressWarning = () => {
    if (!this.state.invalidLocationAddress) return null;
    return (
      <div data-cy="invalid-address-warning">
        <Label role="tooltip" basic color="red" pointing>
          <FormattedMessage {...messages.badAddress} />
        </Label>
      </div>
    );
  }

  renderDeleteMessage = () => {
    const { unpublishedEventCount, publishedEventCount, expiredEventCount } = this.props.location;
    const canDelete = !(publishedEventCount > 0);
    const publishedWarning = publishedEventCount > 0 && messages.deleteWithPublishedEvent;
    const unpublishedWarning = unpublishedEventCount > 0 && messages.deleteWithUnpublishedEvent;
    const expiredWarning = expiredEventCount > 0 && messages.deleteWithExpiredEvent;
    const warning = publishedWarning || unpublishedWarning || expiredWarning || null;
    return (
      <FocusLock returnFocus autoFocus>
        <div>
          <Message warning data-cy="delete-confirm-warning">
            {warning && <FormattedMessage {...warning} />}
            {canDelete && <FormattedMessage {...messages.deleteConfirmMessage} />}
          </Message>
          <div>
            {!canDelete &&
              (
                <Button
                  data-cy="delete-confirm-ok-button"
                  tabIndex={0}
                  primary
                  onClick={() => this.setState({ isDeleting: false })}
                >
                  <FormattedMessage {...messages.ok} />
                </Button>
              )}
            {canDelete &&
              (
                <Button
                  data-cy="delete-confirm-no-button"
                  tabIndex={0}
                  basic
                  color="black"
                  onClick={() => this.setState({ isDeleting: false })}
                >
                  <FormattedMessage {...messages.no} />
                </Button>
              )}
            {canDelete &&
              (
                <Button
                  data-cy="delete-confirm-yes-button"
                  tabIndex={0}
                  primary
                  onClick={() => this.props.deleteLocation(this.props.location)}
                >
                  <FormattedMessage {...messages.yes} />
                </Button>
              )}
          </div>
        </div>
      </FocusLock>
    );
  }

  renderEditForm = () => {
    const namePlaceholder = this.props.intl.formatMessage(messages.name_placeholder);
    const addressPlaceholder = this.props.intl.formatMessage(messages.address_placeholder);
    return (
      <React.Fragment>

        <div styleName="location-form-row location-label required">
          <label htmlFor="location-name"><FormattedMessage {...messages.name} /></label>
        </div>

        <div styleName="location-form-row">
          <Input
            value={this.state.name}
            onChange={(event) => this.onNameChange(event.target.value)}
            name="name"
            maxLength="100"
          >
            <input
              data-cy="new-location-name-input"
              type="text"
              id="location-name"
              name="location-name"
              aria-label={namePlaceholder}
              placeholder={namePlaceholder}
              aria-invalid={this.state.invalidLocationName}
            />
          </Input>
          {this.renderNameWarning()}
        </div>

        <div styleName="address-loader">
          <div styleName="location-form-row location-label required">
            <label htmlFor="location-address"><FormattedMessage {...messages.address} /></label>
          </div>
          <Loader size="tiny" inline active={this.state.fetchingSuggestions} />
        </div>

        <div styleName="location-form-row">
          <Input
            icon
            value={this.state.formattedGeocodeLocation || this.state.address}
            onChange={(event) => this.onAddressChange(event.target.value)}
            name="address"
            styleName="location-address"
            maxLength="100"
            onKeyDown={this.onKeyDownInput}
          >
            <Icon name="search" />
            <input
              data-cy="new-location-address-input"
              type="text"
              id="location-address"
              name="location-address"
              aria-label={addressPlaceholder}
              placeholder={addressPlaceholder}
              aria-invalid={this.state.invalidLocationAddress}
            />
          </Input>
          {this.renderAddressWarning()}
        </div>

      </React.Fragment>
    );
  }

  renderSuggestions = () => {
    if (!this.state.showingSuggestions) return null;
    return (
      <SuggestionList
        suggestions={this.state.suggestions}
        focusLock={this.state.suggestionsFocusLock}
        onSelectionChange={this.onSuggestionSelected}
        onClosePopup={this.hideSuggestions}
        enabledFeatures={this.props.enabledFeatures}
      />
    );
  }

  renderButtons = () => {
    const locationId = (this.props.location || {}).id;
    const actionLabel = locationId ? messages.update : messages.save;
    const { isUpdateButtonDisabled } = this.state;

    return (
      <React.Fragment>

        {locationId ? null :
          (
            <span styleName="checkbox">
              <Checkbox
                data-cy="persist-checkbox"
                id="save_location"
                name="save_location"
                onChange={() => this.setState((previous) => ({ shouldSaveLocation: !previous.shouldSaveLocation }))}
                checked={this.state.shouldSaveLocation}
                label={{
                  htmlFor: 'save_location',
                  children: (<FormattedMessage {...messages.save_location} />),
                }}
              />
            </span>
          )}

        <div styleName="buttons">

          <button
            data-cy="return-button"
            className="return-btn"
            type="button"
            onClick={this.props.showLocations}
            styleName="show-list return-to-list-btn"
          >
            <Icon name="angle double left" />
            <FormattedMessage {...messages.return} />
          </button>

          <Button
            disabled={isUpdateButtonDisabled}
            data-cy="save-button"
            primary
            styleName="save-button"
            onClick={this.validateLocation}
          >
            <FormattedMessage {...actionLabel} />
          </Button>

        </div>

        {locationId && (
          <button
            data-cy="delete-button"
            type="button"
            className="delete-location"
            styleName="delete-location"
            onClick={() => this.setState({ isDeleting: true })}
          >
            <FormattedMessage {...messages.delete} />
          </button>
        )}
      </React.Fragment>
    );
  }

  render() {
    const isLoading = this.state.isDeleting || this.props.isLoading || this.state.fetchingGeocodeLocation;
    return (
      <FocusLock returnFocus autoFocus>

        <Dimmer.Dimmable>

          <Dimmer
            role="progressbar"
            aria-busy={isLoading}
            inverted
            active={isLoading}
          >
            {this.state.isDeleting ?
              this.renderDeleteMessage() :
              <Loader active={isLoading} />}
          </Dimmer>

          {this.renderEditForm()}
          {this.renderSuggestions()}
          {this.props.requestError}
          {this.renderButtons()}

        </Dimmer.Dimmable>

      </FocusLock>
    );
  }
}

export const EditLocationWithIntl = injectIntl(EditLocationComponent);
export default withApollo(EditLocationWithIntl);
